import { Grid } from '@mui/material';
import { t } from 'i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { useCallback, useEffect, useState } from 'react';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { getNextExpireDateMember, updateEnterMember } from 'src/shared/services/memberService';
import { toast } from 'react-toastify';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import * as yup from 'yup';
import { useSchemaActivateMember } from '../schemas/useSchemaActivateMember';
import { EnterMemberRequest } from '../models/enterMemberRequest';

type MemberActiveMemberModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  memberId: number;
  onResignSuccess: () => void;
};

export const MemberActiveMemberModal = (props: MemberActiveMemberModalProps) => {
  const { modalOpen, onClose, memberId, onResignSuccess } = props;
  const isMounted = useIsMounted();

  const [, setIsLoading] = useState<boolean>(true);

  const activateMemberSchema = useSchemaActivateMember();
  type ActivateMemberFormInterface = yup.InferType<typeof activateMemberSchema>;

  const formMethods = useForm<ActivateMemberFormInterface>({
    resolver: yupResolver(activateMemberSchema),
    defaultValues: {
      admDate: dayjs(Date.now()).toDate(),
      payDate: dayjs(Date.now()).toDate(),
    },
  });

  const { handleSubmit, setValue } = formMethods;

  const onCloseHandler = () => {
    onClose();
  };

  const fetchExpireDate = useCallback(async () => {
    try {
      const res = await getNextExpireDateMember(memberId);
      if (isMounted()) {
        setValue('expireDate', dayjs(res.data).toDate());
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error);
      }
    } finally {
      if (isMounted()) {
        setIsLoading(false);
      }
    }
  }, [isMounted, memberId, setValue]);

  useEffect(() => {
    fetchExpireDate();
  }, [fetchExpireDate]);

  const onSubmit = useCallback(
    async (data: ActivateMemberFormInterface) => {
      ConsoleHelper.log('onsubmit', data);
      const dataToPut: EnterMemberRequest = {
        admDate: DateHelper.postDate(data.admDate),
        payDate: DateHelper.postDate(data.payDate),
        expireDate: DateHelper.postDate(data.expireDate),
      };
      ConsoleHelper.log('dataToPut', dataToPut);

      try {
        if (isMounted()) {
          await updateEnterMember(memberId, dataToPut);
          toast.success(t('member:enterMember.saveSuccessMessage'));
          onResignSuccess();
        }
      } catch (error) {
        if (isMounted()) {
          ConsoleHelper.log('error utträde', error);
        }
      }
    },
    [isMounted, memberId, onResignSuccess],
  );

  const onSaveConfirmHandler = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      onClose={onCloseHandler}
      onSave={onSaveConfirmHandler}
      maxWidth='xs'
      showDialogActions
      padding={0}
      dialogTitle={t('member:enterMember.activateMembership')}
      dialogContent={
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container>
              <Grid item xs={12} px={2} pt={4}>
                <ControlledDatePicker name='admDate' required label={t('member:enterMember.admDate')} />
              </Grid>
              <Grid item xs={12} px={2} pt={2}>
                <ControlledDatePicker name='payDate' required label={t('member:enterMember.payDate')} />
              </Grid>
              <Grid item xs={12} px={2} pt={2} pb={4}>
                <ControlledDatePicker name='expireDate' disabled required label={t('member:enterMember.expireDate')} />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      }
    />
  );
};
